// Customer Profiler Chat - Conversational interface for customer profile creation
import { useState, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Bot, Send, Upload, Loader2, Sparkles, AlertCircle } from 'lucide-react'
import { executeAgent } from '@/modules/agents/agent-client'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { scanFileForPII } from '@/lib/pii-upload-guard'
import { PIIApprovalModal } from '@/components/pii/PIIApprovalModal'
import type { PIIDetectionResult } from '@/types/pii'

interface Message {
  role: 'user' | 'assistant'
  content: string
  timestamp: string
}

interface CustomerProfilerChatProps {
  customerId?: string
  onProfileGenerated: (profile: {
    description: string
    configuration_notes: string
    agent_instructions: string
  }) => void
}

export function CustomerProfilerChat({ customerId, onProfileGenerated }: CustomerProfilerChatProps) {
  const { user } = useAuth()
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: "Hi! I'm the Customer Profiler. I'll help you create a comprehensive profile for this D365 customer.\n\nYou can:\n• Upload customer documentation (architecture, configs, licenses)\n• Answer questions about their environment\n• Provide any relevant details\n\nLet's start: What's the customer's D365 F&O version and which modules are they using?",
      timestamp: new Date().toISOString(),
    },
  ])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [uploadedDocs, setUploadedDocs] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [piiResult, setPiiResult] = useState<PIIDetectionResult | null>(null)
  const [pendingFiles, setPendingFiles] = useState<FileList | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const agentId = import.meta.env.VITE_KAYCOO_CUSTOMER_PROFILER_AGENT_ID || 'd365a1b2-dddd-5000-9000-c057096e7f11'

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading) return

    const userMessage: Message = {
      role: 'user',
      content: input,
      timestamp: new Date().toISOString(),
    }

    setMessages((prev) => [...prev, userMessage])
    setInput('')
    setIsLoading(true)
    setError(null)

    try {
      // Build context with conversation history
      const context = messages.map(m => `${m.role}: ${m.content}`).join('\n')
      const prompt = `${context}\nuser: ${input}\n\nDocuments uploaded: ${uploadedDocs.join(', ') || 'None yet'}`

      console.log('[CustomerProfiler] Sending prompt:', prompt)
      const response = await executeAgent(agentId, { input: prompt })
      console.log('[CustomerProfiler] Received response:', response)

      // Extract content from various possible response formats
      const responseAny = response as any
      let content = ''

      // Check for nested data.output structure (Kaycoo platform format)
      if (responseAny.data && responseAny.data.output) {
        content = responseAny.data.output
      } else if (responseAny.output) {
        content = responseAny.output
      } else if (response.response) {
        content = response.response
      } else if (response.result) {
        content = response.result
      } else if (responseAny.content) {
        content = responseAny.content
      } else if (typeof response === 'string') {
        content = response
      } else {
        content = JSON.stringify(response, null, 2)
        console.warn('[CustomerProfiler] Unexpected response format:', response)
      }

      const assistantMessage: Message = {
        role: 'assistant',
        content: content || 'I received your message but had trouble processing it. Please try again.',
        timestamp: new Date().toISOString(),
      }

      setMessages((prev) => [...prev, assistantMessage])
    } catch (err) {
      console.error('[CustomerProfiler] Chat error:', err)
      setError((err as Error).message)

      // Add error message to chat
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: `Error: ${(err as Error).message}. Please check your connection and try again.`,
        timestamp: new Date().toISOString(),
      }])
    } finally {
      setIsLoading(false)
    }
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    // Scan first file for PII before uploading
    try {
      const firstFile = files[0]
      const pii = await scanFileForPII(firstFile)
      if (pii && pii.total_found > 0) {
        setPiiResult(pii)
        setPendingFiles(files)
        return
      }
    } catch (err) {
      console.warn('PII scan failed, proceeding:', err)
    }

    await processFileUpload(files)
  }

  const processFileUpload = async (files: FileList) => {
    setIsLoading(true)
    setError(null)

    try {
      for (const file of Array.from(files)) {
        // Upload to Supabase Storage
        const filePath = `customer-docs/${customerId || 'temp'}/${Date.now()}-${file.name}`
        const { error: uploadError } = await supabase.storage
          .from('customer-documents')
          .upload(filePath, file)

        if (uploadError) throw uploadError

        setUploadedDocs((prev) => [...prev, file.name])

        // Trigger document processing
        try {
          const { data: { session } } = await supabase.auth.getSession()
          if (session && customerId) {
            const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:54321/functions/v1'
            await fetch(`${API_URL}/process-document`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${session.access_token}`,
                'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                filePath,
                customerId,
                organizationId: user?.organizationId,
              }),
            })
          }
        } catch (processError) {
          console.warn('Document processing failed:', processError)
        }

        // Notify agent about uploaded file
        const fileMessage: Message = {
          role: 'user',
          content: `📎 Uploaded: ${file.name}`,
          timestamp: new Date().toISOString(),
        }
        setMessages((prev) => [...prev, fileMessage])
      }

      // Agent acknowledges upload
      const ackMessage: Message = {
        role: 'assistant',
        content: `Great! I've received ${files.length} document(s). I'll analyze them when you click "Generate Profile". What else can you tell me about this customer's environment?`,
        timestamp: new Date().toISOString(),
      }
      setMessages((prev) => [...prev, ackMessage])
    } catch (err) {
      console.error('Upload error:', err)
      setError((err as Error).message)
    } finally {
      setIsLoading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleGenerateProfile = async () => {
    setIsLoading(true)
    setError(null)

    try {
      // Build complete context from conversation
      const conversationContext = messages
        .map(m => `${m.role}: ${m.content}`)
        .join('\n')

      const prompt = `Based on the following conversation and uploaded documents (${uploadedDocs.join(', ')}), generate a comprehensive customer profile.\n\nConversation:\n${conversationContext}\n\nGenerate a JSON response with: description, configuration_notes, and agent_instructions.`

      const response = await executeAgent(agentId, { input: prompt })

      // Parse JSON response - handle nested data.output structure
      const responseAny = response as any
      const content = responseAny.data?.output || responseAny.output || response.response || response.result || ''

      // Try to extract JSON from response
      let profile
      try {
        // Look for JSON in the response
        const jsonMatch = content.match(/\{[\s\S]*"description"[\s\S]*"configuration_notes"[\s\S]*"agent_instructions"[\s\S]*\}/)
        if (jsonMatch) {
          profile = JSON.parse(jsonMatch[0])
        } else {
          // Fallback: treat response as description
          profile = {
            description: content.substring(0, 500),
            configuration_notes: content,
            agent_instructions: 'Based on conversation with Customer Profiler agent.',
          }
        }
      } catch {
        // If JSON parsing fails, use content directly
        profile = {
          description: 'Customer profile generated via conversation.',
          configuration_notes: content,
          agent_instructions: 'Refer to configuration notes for details.',
        }
      }

      onProfileGenerated(profile)

      const successMessage: Message = {
        role: 'assistant',
        content: '✅ Profile generated successfully! The form fields have been populated. You can review and save the customer profile.',
        timestamp: new Date().toISOString(),
      }
      setMessages((prev) => [...prev, successMessage])
    } catch (err) {
      console.error('Generate profile error:', err)
      setError((err as Error).message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex h-[600px] flex-col">
      {/* Chat Messages */}
      <div className="flex-1 space-y-4 overflow-y-auto border rounded-lg p-4 bg-gray-50">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
          >
            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
              msg.role === 'user' ? 'bg-kaycoo-teal-dark' : 'bg-gray-300'
            }`}>
              {msg.role === 'user' ? (
                <span className="text-sm font-medium text-white">You</span>
              ) : (
                <Bot className="h-4 w-4 text-gray-700" />
              )}
            </div>
            <div
              className={`max-w-[80%] rounded-lg px-4 py-2 ${
                msg.role === 'user'
                  ? 'bg-kaycoo-teal-dark text-white'
                  : 'bg-white border'
              }`}
            >
              <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
              <p className={`mt-1 text-xs ${msg.role === 'user' ? 'text-kaycoo-teal-pale' : 'text-gray-500'}`}>
                {new Date(msg.timestamp).toLocaleTimeString()}
              </p>
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-300">
              <Bot className="h-4 w-4 text-gray-700" />
            </div>
            <div className="bg-white border rounded-lg px-4 py-2">
              <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mt-2">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Input Area */}
      <div className="mt-4 space-y-2">
        <div className="flex gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSendMessage()
              }
            }}
            placeholder="Type your message... (Shift+Enter for new line)"
            className="min-h-[60px] resize-none"
            disabled={isLoading}
          />
          <div className="flex flex-col gap-2">
            <Button
              onClick={handleSendMessage}
              disabled={!input.trim() || isLoading}
              className="bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2"
            >
              <Send className="h-4 w-4" />
            </Button>
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              disabled={isLoading}
            >
              <Upload className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={handleGenerateProfile}
            disabled={isLoading || messages.length < 3}
            className="flex-1 bg-gradient-to-r from-kaycoo-teal-dark to-kaycoo-teal text-white"
          >
            <Sparkles className="mr-2 h-4 w-4" />
            Generate Profile from Conversation
          </Button>
        </div>

        {uploadedDocs.length > 0 && (
          <div className="text-xs text-muted-foreground">
            📎 {uploadedDocs.length} document(s) uploaded: {uploadedDocs.join(', ')}
          </div>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.doc,.docx,.txt,.md,.json,.xml"
        onChange={handleFileUpload}
        className="hidden"
      />

      {/* PII Approval Modal */}
      {piiResult && piiResult.total_found > 0 && (
        <PIIApprovalModal
          items={piiResult.pii_found}
          redactedText={piiResult.redacted_text}
          onApprove={() => {
            setPiiResult(null)
            if (pendingFiles) {
              processFileUpload(pendingFiles)
              setPendingFiles(null)
            }
          }}
          onCancel={() => {
            setPiiResult(null)
            setPendingFiles(null)
          }}
        />
      )}
    </div>
  )
}
